import MaterialIconsReact from "material-icons-react";
import { useContext, useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import Card from "../../components/Card";
import Loader from "../../components/Loader";
import { UserContext } from "../../context/UserContext";
import { deleteImages, fetchImageList, getPresignedUrl, uploadImagesToAWS } from "../../services/qcService";
import styles from './ImageTab.module.css';

export function ImageTab() {
  const { setToastList, setShowLoadingFull, uploadList, setUploadList, setFailedList } = useContext(UserContext);
  const [imageList, setImageList] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const params = useParams();

  useEffect(() => {
    getImageList();
  }, [])

  const getImageList = async () => {
    setIsLoading(true);
    try {
      const response = await fetchImageList(params.po_code);
      if (response.status === 200) {
        setImageList(response.message);
      } else {
        showToast({type: 'error', message: response.message, autoClose: false});
      }
    } catch (e) {
      showToast({type: 'error', message: 'Unable to fetch images. Kindly try again!', autoClose: false});
    }
    setIsLoading(false);
  }

  const showToast = ({type, message, action, autoClose = true}) => {
    // show toast message
    let toast = {
      type: type,
      text: message,
      onClick: action,
      autoClose: autoClose
    }
    setToastList([toast]);
  }

  const fileToBinary = (file) => {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.onload = () => {
        const binaryData = reader.result;
        resolve(binaryData);
      };
      reader.onerror = () => {
        reject(new Error('Unable to read the file as binary data'));
      };
      reader.readAsArrayBuffer(file);
    });
  };

  const onFileChange = async (e) => {
    e.preventDefault();
    if (e.target.files.length === 0) {
      return;
    }
    let updatedUploadList = [];
    try {
      let fileNameList = [], fileMap = {};
      for (const file of e.target.files) {
        fileNameList.push(file.name);
        fileMap[file.name] = file;
      }
      const presignedUrlList = await getPresignedUrl({
        po_code: params.po_code,
        imageList: fileNameList
      });

      let index = uploadList.findIndex(x => x.poCode === params.po_code);
      if (index > -1) {
        let itemAttributes = {
          totalUploaded: uploadList[index].totalUploaded + e.target.files.length
        }
        // images upload in progress with this PO
        updatedUploadList = [...uploadList.slice(0,index),
          Object.assign({}, uploadList[index], itemAttributes),
          ...uploadList.slice(index+1)
        ]
      } else {
        updatedUploadList = [...uploadList, {
          poCode: params.po_code,
          uploaded: 0,
          totalUploaded: e.target.files.length
        }]
      }

      setUploadList(updatedUploadList);
      for (const file of e.target.files) {
        await (async function (file) {
          const binaryData = await fileToBinary(file);

          uploadImagesToAWS({
            presignedUrl: presignedUrlList[file.name].URL,
            payload: binaryData
          }).then((response) => {
            setUploadList(list => {
              let index = list.findIndex(x => x.poCode === params.po_code);
              let itemAttributes = {
                uploaded: list[index].uploaded + 1
              }
              return [
                ...list.slice(0,index), 
                Object.assign({}, list[index], itemAttributes),
                ...list.slice(index+1)
              ];
            })
            setImageList(imgList => {
              let index = imgList.findIndex(x => x.file_name === file.name);
              if (index >= 0) {
                // image with same file name exists. overwrite existing image.
                return [...imgList.slice(0, index),
                  {
                    file_name: file.name,
                    pre_signed_url: URL.createObjectURL(file)
                  },
                  ...imgList.slice(index + 1)
                ]
              } else {
                return [...imgList, {
                  file_name: file.name,
                  pre_signed_url: URL.createObjectURL(file)
                }]
              }
            });
          })
              .catch((err) => {
                showToast({
                  type: 'error',
                  message: `Unable to upload image ${file.name}. Kindly try again!`,
                  autoClose: false
                });
              })
        })(file)
      }
    } catch (e) {
      showToast({type: 'error', message: 'Unable to upload images. Kindly try again!', autoClose: false});
    }
  }

  const onDelete = async (image, index) => {
    setShowLoadingFull(true);
    try {
      const response = await deleteImages({
        po_code: params.po_code,
        imageList: [image.file_name]
      })
      if (response.status === 200) {
        showToast({type: 'success', message: response.message});
        setImageList(imgList => {
          let index = imgList.findIndex(x => x.file_name === image.file_name);
          return [...imgList.slice(0,index), ...imgList.slice(index+1)]
        });
      } else {
        showToast({type: 'error', message: response.message, autoClose: false});
      }
    } catch (e) {
      showToast({type: 'error', message: 'Unable to delete image. Kindly try again!', autoClose: false});
    }
    setShowLoadingFull(false);
  }

  return (
      <Card className={styles.cardContainer}>
        <div className={styles.spaceBetween + ' ' + styles.cardHeader}>
          <span className="bold">Panel Images ({imageList.length})</span>
          <label className={styles.fileUpload}>
            <input type="file" multiple onChange={onFileChange} />
            Add more images
          </label>
        </div>
        <div className={styles.imageListWrapper}>
          {!isLoading && imageList.map((image) => {
            return (<div key={image.file_name} className={styles.imageContainer}>
              <img src={image.pre_signed_url} className={styles.imageBox} />
              <div className={styles.spaceBetween + ' ' + styles.footer}>
                <span className={styles.fileName}>{image.file_name}</span>
                {imageList.length > 1 && <MaterialIconsReact data-testid={`delete-${image.file_name}`} icon={'delete'} size={20} onClick={() => onDelete(image)}></MaterialIconsReact>}
              </div>
            </div>)
          })}
          {!isLoading && imageList.length === 0 && <div>Images not found</div>}
          {isLoading && <Loader tip='Fetching panel image list'></Loader>}
        </div>
      </Card>
  )
}